import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { deepCopy } from '@/helpers'
import { TransportCompanyRouteResponseProps } from '@/types'
import { Button, PlusIcon, Text } from '@mercai/clever'

import { MassiveEdit, TransportCompanyRouteItem } from './components'
import { groupedBylogisticRouteUuid } from './transport-company-routes-form.constants'

import type {
  VehiclePricesFormFooterProps,
  VehiclePricesFormProps,
} from './transport-company-routes-form.type'

import styles from './transport-company-routes-form.module.css'

const Root = ({
  onChangeValues,
  values,
  validation,
  vehicles = [],
  isImporter = false,
}: VehiclePricesFormProps) => {
  const { t } = useTranslation('screenLogistic')

  const [isMassiveEdit, setisMassiveEdit] = useState(false)

  const { errors } = validation

  const groupedValues = groupedBylogisticRouteUuid(
    deepCopy(values?.transportCompanyRoutes),
  )

  const filteredVehicles = vehicles.filter((vehicle) =>
    isImporter
      ? vehicle.slug === 'container_20'
      : vehicle.slug !== 'container_20',
  )

  const onHandleValues = (
    index: number,
    key: string,
    value: string | number,
  ) => {
    const valuesAux = deepCopy(values)

    if (valuesAux?.transportCompanyRoutes?.[index]) {
      valuesAux.transportCompanyRoutes[index] = {
        ...valuesAux.transportCompanyRoutes[index],
        [key]: value,
      }
    }

    onChangeValues({ ...valuesAux })
  }

  const onHandleDelete = (index: number, key: string) => {
    if (groupedValues && groupedValues?.[key]?.length <= 1) return null

    const valuesAux = deepCopy(values)

    if (valuesAux?.transportCompanyRoutes?.[index]) {
      valuesAux.transportCompanyRoutes.splice(index, 1)
    }

    onChangeValues({ ...valuesAux })
  }

  const onHandleAdd = (uuid: string) => {
    const value: Partial<TransportCompanyRouteResponseProps> = {
      logisticRouteUuid: uuid,
    }

    const valuesAux = deepCopy(values)

    if (valuesAux?.transportCompanyRoutes) {
      valuesAux.transportCompanyRoutes.push(
        value as TransportCompanyRouteResponseProps,
      )
    }

    onChangeValues({ ...valuesAux })
  }

  const onMassiveEditConfirm = ({
    insurancePrice,
    crossDockingPrice,
  }: {
    insurancePrice?: number | string
    crossDockingPrice?: number | string
  }) => {
    const valuesAux = deepCopy(values)

    if (valuesAux?.transportCompanyRoutes) {
      valuesAux.transportCompanyRoutes = valuesAux.transportCompanyRoutes.map(
        (route) => ({
          ...route,
          ...(insurancePrice !== undefined && {
            insurancePrice: +insurancePrice,
          }),
          ...(crossDockingPrice !== undefined && {
            crossDockingPrice: +crossDockingPrice,
          }),
        }),
      )
    }

    onChangeValues({ ...valuesAux })
    setisMassiveEdit(false)
  }

  return (
    <div className={styles.core}>
      {isImporter && (
        <div>
          {!isMassiveEdit && (
            <Button
              className={styles['massive-edit-button']}
              variant="light"
              size="small"
              onClick={() => setisMassiveEdit(true)}
            >
              {t('drawerLogisticPartnerFormMassiveEditButton')}
            </Button>
          )}

          {isMassiveEdit && (
            <MassiveEdit
              onConfirm={onMassiveEditConfirm}
              onCancel={() => setisMassiveEdit(false)}
            />
          )}
        </div>
      )}
      {Object.keys(groupedValues || {})?.map((key, j) => (
        <div key={`routes-${j}`} className={styles.route}>
          <Text variant="text-large-bold">
            {groupedValues?.[key]?.[0]?.logisticRoute?.name}
          </Text>
          {groupedValues?.[key]?.map((vehiclePrice, i) => (
            <TransportCompanyRouteItem
              key={`route-item-${i}`}
              isImporter={isImporter}
              errors={errors}
              vehiclePrice={vehiclePrice}
              vehicles={filteredVehicles}
              disabledDelete={
                !!(groupedValues && groupedValues?.[key]?.length <= 1)
              }
              onHandleDelete={() => onHandleDelete(vehiclePrice.index, key)}
              onHandleValues={onHandleValues}
            />
          ))}
          <Button
            onClick={() => onHandleAdd(key)}
            size="small"
            variant="ghost"
            className={styles['add-button']}
          >
            {t('vehiclePricesFormAddLabel')}
            <PlusIcon />
          </Button>
        </div>
      ))}
    </div>
  )
}

const Footer = ({
  onNavigateToPreviousStep,
  onSubmit,
  validation,
}: VehiclePricesFormFooterProps) => {
  const { t } = useTranslation('screenLogistic')

  const { isValid, onHandleListenErrors } = validation

  const onHandleSubmit = () => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }

    onHandleListenErrors(false)
    onSubmit()
  }

  return (
    <>
      <Button variant="secondary" onClick={() => onNavigateToPreviousStep()}>
        {t('drawerLogisticPartnerFormReturnButton')}
      </Button>
      <Button onClick={onHandleSubmit}>
        {t('drawerLogisticPartnerFormSubmitButton')}
      </Button>
    </>
  )
}

export const TransportCompanyRoutesForm = {
  Root,
  Footer,
}
